import { useEffect, useMemo, useState } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, TextField, InputAdornment, Snackbar, Alert } from '@mui/material'
import { Add, MoreVert, Edit, Block, CheckCircleOutlined, Search, Sell, Inventory2, AccountBalanceWallet } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listClients, deactivateClient, reactivateClient, type ClientListItem } from '../../api/clienti'
import { errorMessage } from '../../api/client'
import ClientFormDialog from './ClientFormDialog'
import ClientPriceOverridesDialog from './ClientPriceOverridesDialog'
import ClientUsualArticlesDialog from './ClientUsualArticlesDialog'
import ClientBalanceDialog from './ClientBalanceDialog'

export default function ClientsPage() {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const [formTarget, setFormTarget] = useState<ClientListItem | null | 'new'>(null)
  const [pricesTarget, setPricesTarget] = useState<ClientListItem | null>(null)
  const [usualArticlesTarget, setUsualArticlesTarget] = useState<ClientListItem | null>(null)
  const [balanceTarget, setBalanceTarget] = useState<ClientListItem | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: ClientListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['comercial', 'clienti', debouncedSearch], queryFn: () => listClients(debouncedSearch) })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['comercial', 'clienti'] })

  const deactivateMutation = useMutation({ mutationFn: deactivateClient, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const reactivateMutation = useMutation({ mutationFn: reactivateClient, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<ClientListItem>[]>(() => [
    { field: 'code', headerName: 'Cod', width: 100, renderCell: (p) => <code>{p.row.code}</code> },
    { field: 'name', headerName: 'Denumire', flex: 1.3, minWidth: 220 },
    { field: 'cui', headerName: 'CUI', width: 130, renderCell: (p) => p.row.cui ?? '—' },
    { field: 'marketSegment', headerName: 'Segment', width: 120, renderCell: (p) => p.row.marketSegment ?? '—' },
    { field: 'salesAgentName', headerName: 'Agent de vânzări', width: 170, renderCell: (p) => p.row.salesAgentName ?? '—' },
    {
      field: 'creditLimit', headerName: 'Limită credit', width: 130, type: 'number',
      renderCell: (p) => p.row.creditLimit != null ? p.row.creditLimit.toFixed(2).replace('.', ',') : '—',
    },
    {
      field: 'status', headerName: 'Status', width: 100, sortable: false,
      renderCell: (p) => p.row.isActive
        ? <Chip label="Activ" size="small" color="success" variant="outlined" />
        : <Chip label="Inactiv" size="small" variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Comercial</Typography>
        <Typography variant="inherit" color="text.primary">Clienți</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Typography variant="h2">Clienți</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Adaugă client</Button>
      </Box>

      <TextField
        size="small" placeholder="Caută după cod, denumire sau CUI…" value={search}
        onChange={(e) => setSearch(e.target.value)} sx={{ maxWidth: 360 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <ClientFormDialog client={formTarget} onClose={() => setFormTarget(null)} />
      <ClientPriceOverridesDialog client={pricesTarget} onClose={() => setPricesTarget(null)} />
      <ClientUsualArticlesDialog client={usualArticlesTarget} onClose={() => setUsualArticlesTarget(null)} />
      <ClientBalanceDialog client={balanceTarget} onClose={() => setBalanceTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="preturi" onClick={() => { setPricesTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Sell fontSize="small" /></ListItemIcon><ListItemText>Prețuri negociate</ListItemText>
          </MenuItem>,
          <MenuItem key="produse-uzuale" onClick={() => { setUsualArticlesTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Inventory2 fontSize="small" /></ListItemIcon><ListItemText>Produse uzuale</ListItemText>
          </MenuItem>,
          <MenuItem key="sold" onClick={() => { setBalanceTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><AccountBalanceWallet fontSize="small" /></ListItemIcon><ListItemText>Sold client</ListItemText>
          </MenuItem>,
          menuAnchor.row.isActive ? (
            <MenuItem key="deactivate" onClick={() => { closeMenu(); deactivateMutation.mutate(menuAnchor.row.id) }}>
              <ListItemIcon><Block fontSize="small" /></ListItemIcon><ListItemText>Dezactivare</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem key="reactivate" onClick={() => { closeMenu(); reactivateMutation.mutate(menuAnchor.row.id) }}>
              <ListItemIcon><CheckCircleOutlined fontSize="small" /></ListItemIcon><ListItemText>Reactivare</ListItemText>
            </MenuItem>
          ),
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
